import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/site/Logo";
import AdminContent from "@/pages/admin/AdminContent";
import { Plus, Pencil, Trash2, X, LogOut, Mail, ExternalLink } from "lucide-react";

const EMPTY = { title: "", category: "SAS", excerpt: "", cover_image: "", content: "", published: true };
const CATS = ["SAS®", "R", "SQL", "Statistics", "Career", "General"];
const FIELD =
  "w-full border border-white/15 bg-navy px-3 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-brand-blue";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} object being edited/created
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([api.get("/admin/blog"), api.get("/admin/contacts")]);
      setPosts(p.data);
      setContacts(c.data);
    } catch {
      toast.error("Failed to load data");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (!editing.title || !editing.content || !editing.excerpt) {
      toast.error("Title, excerpt and content are required");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/blog/${editing.id}`, editing);
        toast.success("Post updated");
      } else {
        await api.post("/admin/blog", editing);
        toast.success("Post created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removePost = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success("Post deleted");
    load();
  };

  const removeContact = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/admin/contacts/${id}`);
    toast.success("Enquiry deleted");
    load();
  };

  return (
    <div className="min-h-screen bg-navy" data-testid="admin-dashboard">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="hidden font-mono text-xs uppercase tracking-[0.2em] text-slate-500 sm:inline">/ CMS</span>
          </div>
          <div className="flex items-center gap-5">
            <span className="hidden font-mono text-xs text-slate-400 sm:inline">{user?.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-brand-magenta"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="flex items-center gap-3">
          {[
            ["posts", `Posts (${posts.length})`],
            ["enquiries", `Enquiries (${contacts.length})`],
            ["content", "Site Content"],
          ].map(([k, label]) => (
            <button
              key={k}
              data-testid={`admin-tab-${k}`}
              onClick={() => setTab(k)}
              className={`border px-5 py-2 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                tab === k ? "border-brand-blue bg-brand-blue text-white" : "border-white/15 text-slate-400 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
          {tab === "posts" && (
            <button
              onClick={() => setEditing({ ...EMPTY })}
              data-testid="admin-new-post"
              className="ml-auto flex items-center gap-2 bg-brand-blue px-5 py-2 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-magenta"
            >
              <Plus size={14} /> New post
            </button>
          )}
        </div>

        {/* POSTS */}
        {tab === "posts" && (
          <div className="mt-10 divide-y divide-white/10 border border-white/10">
            {posts.length === 0 && <p className="p-8 font-mono text-sm text-slate-500">No posts yet.</p>}
            {posts.map((p) => (
              <div key={p.id} data-testid={`admin-post-${p.slug}`} className="flex items-center justify-between gap-4 bg-surface/40 p-5">
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-magenta">{p.category}</span>
                    {!p.published && (
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-500">Draft</span>
                    )}
                  </div>
                  <h3 className="mt-1 truncate font-display text-lg text-white">{p.title}</h3>
                  <p className="truncate font-mono text-[11px] text-slate-500">/{p.slug}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="border border-white/15 p-2 text-slate-400 hover:text-white" title="View">
                    <ExternalLink size={15} />
                  </a>
                  <button onClick={() => setEditing(p)} data-testid={`admin-edit-${p.slug}`} className="border border-white/15 p-2 text-slate-300 hover:border-brand-blue hover:text-white">
                    <Pencil size={15} />
                  </button>
                  <button onClick={() => removePost(p.id)} data-testid={`admin-delete-${p.slug}`} className="border border-white/15 p-2 text-slate-300 hover:border-red-500 hover:text-red-400">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ENQUIRIES */}
        {tab === "enquiries" && (
          <div className="mt-10 grid gap-4" data-testid="admin-enquiries">
            {contacts.length === 0 && <p className="font-mono text-sm text-slate-500">No enquiries yet.</p>}
            {contacts.map((c) => (
              <div key={c.id} className="border border-white/10 bg-surface/40 p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl text-white">{c.name}</h3>
                    <a href={`mailto:${c.email}`} className="flex items-center gap-2 font-mono text-xs text-brand-blue">
                      <Mail size={12} /> {c.email}
                    </a>
                  </div>
                  <button onClick={() => removeContact(c.id)} className="border border-white/15 p-2 text-slate-300 hover:border-red-500 hover:text-red-400">
                    <Trash2 size={15} />
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-x-6 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
                  <span>{c.subject}</span>
                  {c.course && <span>Course: {c.course}</span>}
                </div>
                <p className="mt-4 leading-relaxed text-slate-300">{c.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "content" && <AdminContent />}
      </div>

      {/* EDITOR MODAL */}
      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm"
            onClick={() => setEditing(null)}
          >
            <motion.form
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 30 }}
              onClick={(e) => e.stopPropagation()}
              onSubmit={save}
              data-testid="admin-post-form"
              className="my-8 w-full max-w-2xl border border-white/15 bg-surface p-8"
            >
              <div className="flex items-center justify-between">
                <h2 className="font-display text-2xl text-white">{editing.id ? "Edit post" : "New post"}</h2>
                <button type="button" onClick={() => setEditing(null)} className="text-slate-400 hover:text-white">
                  <X size={20} />
                </button>
              </div>

              <div className="mt-8 space-y-5">
                <input data-testid="post-title" className={FIELD} placeholder="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
                <div className="grid grid-cols-2 gap-4">
                  <select data-testid="post-category" className={FIELD} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                    {CATS.map((c) => <option key={c} value={c} className="bg-navy">{c}</option>)}
                  </select>
                  <label className="flex cursor-pointer items-center gap-3 border border-white/15 px-3 py-2.5 text-sm text-slate-300">
                    <input data-testid="post-published" type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} />
                    Published
                  </label>
                </div>
                <input data-testid="post-cover" className={FIELD} placeholder="Cover image URL (optional)" value={editing.cover_image || ""} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} />
                <textarea data-testid="post-excerpt" rows={2} className={`${FIELD} resize-none`} placeholder="Short excerpt" value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} />
                <textarea data-testid="post-content" rows={8} className={`${FIELD} resize-none`} placeholder="Content (use blank lines for paragraphs)" value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} />
              </div>

              <div className="mt-8 flex justify-end gap-3">
                <button type="button" onClick={() => setEditing(null)} className="border border-white/15 px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white">
                  Cancel
                </button>
                <button type="submit" disabled={saving} data-testid="post-save" className="bg-brand-blue px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-magenta disabled:opacity-60">
                  {saving ? "Saving…" : "Save post"}
                </button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
